import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckoutProducts } from "../minor-components/CheckoutProducts";
import { CheckoutUserDetails } from "../minor-components/CheckoutUserDetails";
import { axiosInstance } from '../../constants/axiosInstance';
import { Loader } from '../minor-components/Loader';
import { useAlert } from 'react-alert';
import { Footer } from "./Footer";
import { Navbar } from "./Navbar";
import { ShoppingBag, CreditCard } from "lucide-react";

export const Checkout = ({ setSearchQuery, searchQuery }) => {
  const [loading, setLoading] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartUser, setCartUser] = useState([]);
  const [subTotalPrice, setSubTotalPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [specialInstructions, setSpecialInstructions] = useState("");  // NEW: For special instructions
  const [paymentMethod, setPaymentMethod] = useState("");  // NEW: For payment method
  const[deliveryCharges, setDeliveryCharges]=useState(0);

  let navigate = useNavigate();
  const alert = useAlert();

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/api/v1/order/getcart", {
        headers: {
          "Authorization": localStorage.getItem('token')
        }
      });
      //console.log('Checkout response', response);
      setCartItems(response.data.data.details);
      setSubTotalPrice(response.data.data.subTotal);
      setTotalProducts(response.data.data.totalProducts);
      setTotalPrice(response.data.data.totalPrice);
      setFinalPrice(response.data.data.totalPrice);
      setDeliveryCharges(response.data.data.deliveryCharges);
      setCartUser(response.data.data.userId);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      fetchCart();
    } else {
      navigate("/login");
    }
  }, [fetchCart, navigate]);

  const handleDelivery = async (day) => {
    console.log(day, 'day');
  };

  const handleApplyDiscount = async () => {
    try {
      const response = await axiosInstance.post("/api/v1/discount/apply", {
        code: discountCode,
        totalPrice: totalPrice,
      }, {
        headers: {
          "Authorization": localStorage.getItem('token')
        }
      });

      if (response.data.success) {
        setDiscountAmount(response.data.data.discountAmount);
        setFinalPrice(response.data.data.finalPrice);
        alert.show("Discount applied successfully");
      }
    } catch (error) {
      alert.show(error.response.data.message);
    }
  };

  // Updated to include new fields like special instructions and payment method
  const handleSubmit = async (deliveryDay, finalPrice, deliveryCharges, deliveryAddress, postalCode, paymentMethod, coordinates, discountCode, discountAmount, specialInstructions) => {
    if (paymentMethod === '' && deliveryDay === 'Same Day Delivery') {
      alert("Please Select Payment Method");
      return;
    }
  
    // Ensure coordinates are in the correct format [longitude, latitude]
    let formattedCoordinates;
    if (coordinates && Array.isArray(coordinates)) {
      // If coordinates are already an array, swap them to [longitude, latitude]
      formattedCoordinates = [coordinates[1], coordinates[0]];
    } else if (coordinates && typeof coordinates === 'object') {
      // If coordinates are an object with lat/lng properties
      formattedCoordinates = [coordinates.lng || coordinates.longitude, coordinates.lat || coordinates.latitude];
    } else {
      alert.show("Invalid coordinates format");
      return;
    }
  
    const order = {
      details: cartItems,
      totalPrice: parseFloat(finalPrice),
      deliveryCharges: parseFloat(deliveryCharges),
      deliveryType: deliveryDay,
      subTotal: parseFloat(subTotalPrice),
      totalProducts: totalProducts,
      formattedAddress: deliveryAddress,
      postalCode: postalCode,
      paymentMethod: paymentMethod,
      geometry: formattedCoordinates, // Use the formatted coordinates
      discountCode: discountCode,
      discountAmount: parseFloat(discountAmount || 0),
      finalPrice: parseFloat(finalPrice),
      specialInstructions: specialInstructions
    };
  
    try {
      console.log("Sending order data:", order);
            // Place order
            const response = await axiosInstance.post("/api/v1/order/placeorder", order, {
              headers: {
                "Authorization": localStorage.getItem('token')
              }
            });
      
            if (!response.data.success) {
              throw new Error(response.data.message || "Failed to place order");
            }
      
            // Delete cart only if order was successful
            const deleteResponse = await axiosInstance.delete('/api/v1/order/deletecart', {
              headers: {
                "Authorization": localStorage.getItem('token')
              }
            });
      
            if (!deleteResponse.data.success) {
              throw new Error("Order placed but failed to clear cart");
            }
      
            // Success path
            alert.show("Order Placed Successfully");
            if (setSearchQuery) {
              setSearchQuery(prev => !prev);
            }
            navigate('/', { replace: true });
      
          } catch (err) {
            console.error("Error details:", err.response ? err.response.data : err.message);
            alert.show(err.response?.data?.message || err.message || "An unexpected error occurred");
          }
        };
      

  const handleItem = async (item, action) => {
    setLoading(true);
    const details = {
      productId: item.id,
      quantity: item.quantity,
    };
    let url = action === 'p' ? '/api/v1/order/addtocart' : '/api/v1/order/decreasecartquantity';

    try {
      await axiosInstance.post(url, details, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": localStorage.getItem('token')
        }
      });
      await fetchCart(); // Refresh cart data after item update
    } catch (err) {
      console.error(err);
      alert.show("Error updating cart");
    } finally {
      setLoading(false);
    }
  };

  const handleExtraItem = async (item, action) => {
    setLoading(true);
    const details = {
      productId: item.productId,
      quantity: item.quantity,
      extras: item.extras
    };
    let url = action === 'p' ? '/api/v1/order/addtocart' : '/api/v1/order/decreaseextrasquantity';

    try {
      await axiosInstance.post(url, details, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": localStorage.getItem('token')
        }
      });
      await fetchCart(); // Refresh cart data after item update
    } catch (err) {
      console.error(err);
      alert.show("Error updating cart");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!loading ? (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
          {cartItems.length !== 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
            >
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
                <div className="flex items-center space-x-2">
                  <ShoppingBag className="w-6 h-6 text-indigo-600" />
                  <span className="text-lg font-medium">{totalProducts} items</span>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-12">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-sm p-6 space-y-6"
                >
                  <div className="space-y-4">
                    {cartItems.map((product, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="transform transition-all duration-200 hover:scale-[1.02]"
                      >
                        <CheckoutProducts
                          cartProductId={product.productId._id}
                          cartProductName={product.productId.name}
                          cartProductImage={product.productId.productPhoto}
                          cartProductPrice={product.productId.price}
                          cartProductBrand={product.productId.brand}
                          cartExtras={product.extras}
                          cartProductCategory={product.productId.category}
                          cartProductDescription={product.productId.description}
                          cartProductType={product.productId.type}
                          cartProductQuantity={product.quantity}
                          getItem={handleItem}
                          getExtraItem={handleExtraItem}
                        />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-2xl shadow-sm p-6"
                >
                  <div className="flex items-center space-x-2 mb-6">
                    <CreditCard className="w-6 h-6 text-indigo-600" />
                    <h2 className="text-2xl font-semibold">Payment Details</h2>
                  </div>
                  <CheckoutUserDetails
                    handleSubmit={handleSubmit}
                    handleDeliveryDay={handleDelivery}
                    cartUser={cartUser}
                    totalPrice={totalPrice}
                    subTotalPrice={subTotalPrice}
                    deliveryCharges={deliveryCharges}
                    setDeliveryCharges={setDeliveryCharges}
                    discountcode={discountCode}
                    setDiscountCode={setDiscountCode}
                    handleApplyDiscount={handleApplyDiscount}
                    discountAmount={discountAmount}
                    finalPrice={finalPrice}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    specialInstructions={specialInstructions}
                    setSpecialInstructions={setSpecialInstructions}
                  />
                </motion.div>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center min-h-[60vh] space-y-6"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <ShoppingBag className="w-24 h-24 text-gray-300" />
              </motion.div>
              <h2 className="text-3xl font-bold text-gray-900">Your cart is empty</h2>
              <p className="text-gray-500">Add some items to get started!</p>
              <button 
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium transform transition-all duration-200 hover:scale-105 hover:bg-indigo-700"
              >
                Continue Shopping
              </button>
            </motion.div>
          )}
          <Footer />
        </div>
      ) : (
        <div className="min-h-screen flex items-center justify-center">
          <Loader />
        </div>
      )}
    </>
  );
};
